import { ROLES } from "../config/roles";

/** JSON map: { "seller": "12", "buyer": "34", "freight": "5", "admin": "1" } — used for API payloads when one account has multiple role entities. */
const ROLE_ENTITY_MAP_KEY = "roleEntityIds";

export const readRoleEntityMap = () => {
    try {
        const raw = localStorage.getItem(ROLE_ENTITY_MAP_KEY);
        if (!raw) return {};
        const o = JSON.parse(raw);
        return o && typeof o === "object" && !Array.isArray(o) ? o : {};
    } catch {
        return {};
    }
};

export const writeRoleEntityMap = (map) => {
    localStorage.setItem(ROLE_ENTITY_MAP_KEY, JSON.stringify(map));
};

const mergeEntityId = (map, roleKey, id) => {
    if (id == null || id === "") return;
    map[String(roleKey).toLowerCase()] = String(id);
};

/** Keep legacy `user` key aligned with seller entity id (many seller APIs still read it). */
const syncLegacySellerUserStorage = () => {
    const sid = getEntityIdForRole(ROLES.SELLER);
    if (sid) localStorage.setItem("user", sid);
};

/**
 * Call after successful login with `data` from verify-otp / register / admin login.
 *
 * Matches your backend `roleTables` model:
 * - seller/buyer: prefer master ids (`seller_id` / `buyer_id`) for `/api/seller/*` & buyer APIs;
 *   if omitted, falls back to `employee_id` (seller_employees / buyer_employees row id).
 * - freight: `freight_users.id` — use `employee_id` from login/verify or `freight_user_id`.
 * - admin: `admins.id` — `employee_id` from admin login.
 *
 * Multi-role on one mobile: best is `role_entity_ids` with all keys, e.g.
 * `{ "seller": "<master_or_employee_id>", "buyer": "...", "freight": "...", "admin": "..." }`
 */
export const applyLoginContext = (payload = {}) => {
    const map = { ...readRoleEntityMap() };
    const role = String(payload.role || "").toLowerCase();

    const fromApi =
        payload.role_entity_ids ||
        payload.role_entities ||
        payload.entities ||
        payload.entity_by_role;

    if (fromApi && typeof fromApi === "object" && !Array.isArray(fromApi)) {
        Object.entries(fromApi).forEach(([k, v]) => mergeEntityId(map, k, v));
    }

    if (payload.seller_id != null) mergeEntityId(map, ROLES.SELLER, payload.seller_id);
    if (payload.buyer_id != null) mergeEntityId(map, ROLES.BUYER, payload.buyer_id);
    if (payload.freight_user_id != null) mergeEntityId(map, ROLES.FREIGHT, payload.freight_user_id);

    if (role === ROLES.ADMIN && payload.employee_id != null) {
        mergeEntityId(map, ROLES.ADMIN, payload.employee_id);
    }

    if (role === ROLES.FREIGHT && payload.employee_id != null && !map[ROLES.FREIGHT]) {
        mergeEntityId(map, ROLES.FREIGHT, payload.employee_id);
    }

    if (role === ROLES.SELLER && payload.employee_id != null && !map[ROLES.SELLER]) {
        mergeEntityId(map, ROLES.SELLER, payload.employee_id);
    }

    if (role === ROLES.BUYER && payload.employee_id != null && !map[ROLES.BUYER]) {
        mergeEntityId(map, ROLES.BUYER, payload.employee_id);
    }

    const primary = payload.entity_id ?? payload.user_id ?? payload.context_id;
    if (role && primary != null && !map[role]) {
        mergeEntityId(map, role, primary);
    }

    writeRoleEntityMap(map);

    const accountId = payload.user_id ?? payload.employee_id;
    if (accountId != null) {
        localStorage.setItem("userId", String(accountId));
    }

    syncLegacySellerUserStorage();
};

/**
 * Entity id for API calls for a given role (not always the same as `userId` for multi-role users).
 */
export const getEntityIdForRole = (role) => {
    const r = String(role || "").toLowerCase();
    const map = readRoleEntityMap();
    if (map[r]) return map[r];
    return (
        localStorage.getItem("userId") ||
        (typeof localStorage.getItem("user") === "string"
            ? localStorage.getItem("user")
            : "") ||
        ""
    );
};

export const getEntityIdForCurrentRole = () => {
    return getEntityIdForRole(getRole() || ROLES.SELLER);
};

export const clearRoleEntityMap = () => {
    localStorage.removeItem(ROLE_ENTITY_MAP_KEY);
};

export const getRole = () => {
    return localStorage.getItem("currentRole");
};

export const getAvailableRoles = () => {
    return localStorage.getItem("availableRoles");
}

export const setAvailableRoles = (roles) => {
    return localStorage.setItem("availableRoles", roles)
}

export const setRole = (role) => {
    localStorage.setItem("currentRole", role);
    syncLegacySellerUserStorage();
};

export const clearRole = () => {
    localStorage.removeItem("currentRole");
};

export const getUser = () => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
};

export const getUserId = () => {
    const user = getUser();
    return user?.id || null;
};